import { useCallback, useEffect, useState } from 'react';
import { FormularioCompromisso } from '../componentes/FormularioCompromisso.tsx';
import { ItemCompromisso } from '../componentes/ItemCompromisso.tsx';
import { avisar, avisarErro } from '../componentes/Avisos.tsx';
import { Icone } from '../componentes/Icone.tsx';
import { ErroDaApi, api, type Compromisso, type Feriado, type Painel } from '../lib/api.ts';
import { usarEhMaster } from '../lib/papel.ts';
import {
  nomeDoMes,
  porExtenso,
  primeiroDia,
  semanasDoMes,
  somarMeses,
  ultimoDia,
} from '../lib/datas.ts';
import '../estilos/calendario.css';
/* ⚠️ **`clientes.css` antes de `sistema.css`, e não é engano** — 14/09/2026.
   O Vite junta o CSS na ordem em que as páginas aparecem em `App.tsx`, e esta
   é a primeira. Importar só `sistema.css` aqui o puxaria para antes de
   `clientes.css`, e empate de seletor mudaria de dono na ficha do cliente. */
import '../estilos/clientes.css';
import '../estilos/sistema.css';
import '../estilos/calendario-2026.css';

const CABECALHO_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

type Formulario = { modo: 'novo'; data: string } | { modo: 'editar'; alvo: Compromisso } | null;

export function Calendario({ aoExpirarSessao }: { aoExpirarSessao: () => void }) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [mes, setMes] = useState<string | null>(null);
  const [doMes, setDoMes] = useState<Compromisso[]>([]);
  const [feriados, setFeriados] = useState<Map<string, Feriado>>(new Map());
  const [erro, setErro] = useState<string | null>(null);
  const [formulario, setFormulario] = useState<Formulario>(null);
  const ehMaster = usarEhMaster();

  /** Sessão que caiu enquanto a tela estava aberta volta para o login. */
  const tratar = useCallback(
    (e: unknown) => {
      if (e instanceof ErroDaApi && e.status === 401) return aoExpirarSessao();
      avisarErro(e);
      setErro(e instanceof Error ? e.message : 'Algo não funcionou.');
    },
    [aoExpirarSessao],
  );

  const recarregar = useCallback(async () => {
    try {
      setErro(null);
      const novoPainel = await api.painel();
      setPainel(novoPainel);
      // O mês corrente vem do servidor, em horário de São Paulo — nunca do
      // relógio de quem abriu a tela.
      const mesAtual = mes ?? novoPainel.hoje.slice(0, 7);
      if (!mes) setMes(mesAtual);
      const de = primeiroDia(mesAtual);
      const ate = ultimoDia(mesAtual);
      const [{ compromissos }, { feriados: lista }] = await Promise.all([
        api.doMes(de, ate),
        api.feriados(de, ate),
      ]);
      setDoMes(compromissos);
      // Feriado do mesmo dia com dois nomes não existe no calendário oficial;
      // o Map guarda um por data e pronto.
      setFeriados(new Map(lista.map((f) => [f.data, f])));
    } catch (e) {
      tratar(e);
    }
  }, [mes, tratar]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function agir(acao: () => Promise<unknown>) {
    try {
      await acao();
      await recarregar();
    } catch (e) {
      tratar(e);
    }
  }

  if (!painel || !mes) {
    return <div className="carregando">Carregando o calendário…</div>;
  }

  const semanas = semanasDoMes(mes);
  const porDia = new Map<string, Compromisso[]>();
  for (const c of doMes) {
    const lista = porDia.get(c.data) ?? [];
    lista.push(c);
    porDia.set(c.data, lista);
  }
  const diasComCoisa = [...porDia.keys()].sort();

  return (
    /*
      ⭐ **Sistema visual 2026, parte 4 — o Calendário, 14/09/2026.** A folha
      de identidade com o mês e a navegação; a grade numa folha só, com a
      legenda das cores no topo. ⛔ **Mudou a aparência, não o que se clica:**
      o dia abre "anotar", a pílula abre "editar", e no celular a mesma lista.
    */
    <div className="calendario-2026">
      {erro && <p className="erro">{erro}</p>}

      <section className="s-folha s-identidade calendario-identidade">
        <div className="s-monograma calendario-monograma" aria-hidden="true">
          <Icone nome="calendario" tamanho={28} />
        </div>
        <div className="calendario-identidade-texto">
          <span className="s-sobrelinha">Calendário</span>
          <h1 className="titulo">{nomeDoMes(mes)}</h1>
          <p className="calendario-apoio">Prazos, reuniões e feriados de São Paulo</p>
        </div>
        <div className="calendario-acoes">
          <div className="navegacao-mes" role="group" aria-label="Mês">
            <button
              type="button"
              onClick={() => setMes(somarMeses(mes, -1))}
              aria-label="Mês anterior"
              title="Mês anterior"
            >
              <Icone nome="avancar" tamanho={16} />
            </button>
            <button type="button" onClick={() => setMes(painel.hoje.slice(0, 7))}>
              Hoje
            </button>
            <button
              type="button"
              onClick={() => setMes(somarMeses(mes, 1))}
              aria-label="Próximo mês"
              title="Próximo mês"
            >
              <Icone nome="avancar" tamanho={16} />
            </button>
          </div>
          <button
            type="button"
            className="botao-primario s-botao"
            onClick={() => setFormulario({ modo: 'novo', data: painel.hoje })}
          >
            <Icone nome="mais" tamanho={15} />
            Anotar
          </button>
        </div>
      </section>

      {/* ── O mês ─────────────────────────────────────────────────── */}
      <section className="s-folha calendario-mes" aria-label={nomeDoMes(mes)}>
        {/* A legenda das cores: sem ela, o ouro da reunião e o do ponto
            facultativo se confundem. O texto é o que carrega a informação. */}
        <div className="s-folha-topo calendario-legenda" aria-hidden="true">
          <span className="legenda legenda-entrega">Entrega</span>
          <span className="legenda legenda-reuniao">Reunião</span>
          <span className="legenda legenda-interno">Interno</span>
          <span className="legenda legenda-feriado">Feriado</span>
          <span className="legenda legenda-facultativo">Ponto facultativo</span>
        </div>

        {/* Computador: a grade. Rola dentro do próprio bloco, nunca a página. */}
        <div className="grade-rolagem">
          <table className="grade">
            <thead>
              <tr>
                {CABECALHO_SEMANA.map((dia) => (
                  <th key={dia} className="rotulo">
                    {dia}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {semanas.map((semana, i) => (
                <tr key={i}>
                  {semana.map((data, j) => (
                    <td
                      key={data ?? `vazio-${j}`}
                      className={
                        'celula' +
                        (data === null ? ' celula-fora' : '') +
                        (data === painel.hoje ? ' celula-hoje' : '') +
                        (data && feriados.has(data)
                          ? feriados.get(data)!.tipo === 'facultativo'
                            ? ' celula-facultativo'
                            : ' celula-feriado'
                          : '')
                      }
                    >
                      {data && (
                        <>
                          <button
                            type="button"
                            className="celula-dia dado"
                            onClick={() => setFormulario({ modo: 'novo', data })}
                            title="Anotar neste dia"
                            aria-current={data === painel.hoje ? 'date' : undefined}
                          >
                            {Number(data.slice(8))}
                          </button>
                          {feriados.has(data) && (
                            <span
                              className={
                                'marca-feriado marca-' + feriados.get(data)!.tipo
                              }
                              title={feriados.get(data)!.nome}
                            >
                              {feriados.get(data)!.nome}
                            </span>
                          )}
                          {/* ⛔ **Abrir para editar é do master — 19/09/2026.** A
                              etiqueta abria "Editar compromisso" para todos, e
                              o servidor recusava a gravação do colaborador. Para
                              ele, a etiqueta é só leitura; concluir continua onde
                              era (Hoje e a lista do mês no celular). */}
                          {(porDia.get(data) ?? []).map((c) =>
                            ehMaster ? (
                              <button
                                key={c.id}
                                type="button"
                                className={
                                  `pilula pilula-${c.tipo}` + (c.concluido ? ' pilula-feita' : '')
                                }
                                onClick={() => setFormulario({ modo: 'editar', alvo: c })}
                                title={c.titulo}
                              >
                                {c.titulo}
                              </button>
                            ) : (
                              <span
                                key={c.id}
                                className={
                                  `pilula pilula-${c.tipo}` + (c.concluido ? ' pilula-feita' : '')
                                }
                                title={c.titulo}
                              >
                                {c.titulo}
                              </span>
                            ),
                          )}
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Celular: a mesma informação em lista. Grade de 7 colunas em tela
            estreita ou fica ilegível, ou obriga a página a rolar de lado. */}
        <div className="mes-lista">
          {diasComCoisa.length === 0 ? (
            <p className="vazio">Nada marcado neste mês.</p>
          ) : (
            diasComCoisa.map((data) => (
              <div key={data} className="dia-lista">
                <p className={'dia-lista-data' + (data === painel.hoje ? ' e-hoje' : '')}>
                  {porExtenso(data)}
                  {feriados.has(data) && (
                    <span className={'marca-feriado marca-' + feriados.get(data)!.tipo}>
                      {feriados.get(data)!.nome}
                    </span>
                  )}
                </p>
                {(porDia.get(data) ?? []).map((c) => (
                  <ItemCompromisso
                    key={c.id}
                    compromisso={c}
                    semData
                    desenho="linha"
                    atrasado={!c.concluido && data < painel.hoje}
                    aoConcluir={() => agir(() => api.concluir(c.id))}
                    aoDesmarcar={() => agir(() => api.desmarcar(c.id))}
                    aoEditar={() => setFormulario({ modo: 'editar', alvo: c })}
                    aoApagar={() => agir(() => api.apagar(c.id))}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </section>

      {formulario && (
        <FormularioCompromisso
          inicial={formulario.modo === 'editar' ? formulario.alvo : null}
          dataSugerida={formulario.modo === 'novo' ? formulario.data : undefined}
          aoFechar={() => setFormulario(null)}
          aoSalvar={async (dados) => {
            if (formulario.modo === 'editar') {
              await api.editar(formulario.alvo.id, dados);
              avisar('Compromisso atualizado.');
            } else {
              await api.criar(dados);
              avisar('Compromisso anotado.');
            }
            setFormulario(null);
            await recarregar();
          }}
        />
      )}
    </div>
  );
}
